import React, { useCallback, useState } from 'react';
import flag from 'v2/helpers/flags';
import isEmpty from 'lodash/isEmpty';
import { PENDING } from 'v2/helpers/status/orders';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import AssignedApproversAvatars from './AssignedApproversAvatars';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {
  themeTable,
  AvtarGridContainer,
  CompanyGridContainer,
  StatusGridContainer,
} from 'v2/apps/clink/pages/orders/Mui.Components';
import MuiDropdownButton from 'v2/apps/clink/pages/shared/MuiDropdown';
import { getAccountLogo } from 'v2/helpers/user';
import parseCurrency, { currencyConfig } from 'v2/helpers/currency';
import Modal from 'v2/apps/clink/pages/orders/subcontractors/modal';
import getActions from 'v2/apps/clink/pages/orders/subcontractors/useActions';
import LogsModal from 'v2/apps/shared/components/logs-modal';
import Collapse from '@mui/material/Collapse';
import ApprovalExpandablePanel from 'v2/apps/shared/components/approval-expandable-panel';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RejectionAcknowledgeModal from '../../projects/RejectionAcknowledgeModal';
import RejectionDetailContent from '../../projects/RejectionDetailContent';
import { clinkRed } from 'v2/constants/colors';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import PropTypes from 'prop-types';
import i18next from 'v2/helpers/i18n';
import mapAssignedApproversToLevels from 'v2/helpers/approvers';
import { connect } from 'react-redux';
import LoiRow from './LoiRow';
import IssueOrderForm from 'v2/apps/clink/pages/tender-analysis/summary/actions/Form';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import {
  isOrderVisible,
  canCreateFinalOrder,
} from 'v2/apps/clink/pages/orders/loiSelectors';

const document_column =
  (flag('DELETE_DRAFT_ORDER') && [{ id: 2, entry: 'order-page-document' }]) ||
  [];
const headEntries = [
  { id: 0, entry: 'order-page-type' },
  { id: 1, entry: 'order-page-reference' },
  ...document_column,
  { id: 3, entry: 'order-page-order-date' },
  { id: 4, entry: 'order-page-company' },
  { id: 5, entry: 'order-page-value' },
  { id: 6, entry: 'order-page-status' },
];

const createdAt = (date) =>
  date ? String(moment(new Date(date)).format('DD/MM/YYYY')) : 'N/A';

const WithdrawApprovalDialog = ({
  open,
  onClose,
  onConfirm,
  loading = false,
}) => (
  <Dialog
    open={open}
    onClose={loading ? undefined : onClose}
    data-testid="withdraw-approval-dialog"
  >
    <DialogTitle>{i18next.t('withdraw-order-approval-title')}</DialogTitle>
    <DialogContent>
      <DialogContentText>
        {i18next.t('withdraw-order-approval-description')}
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button
        data-testid="withdraw-approval-cancel"
        onClick={onClose}
        disabled={loading}
      >
        {i18next.t('cancel')}
      </Button>
      <Button
        data-testid="withdraw-approval-confirm"
        onClick={onConfirm}
        variant="contained"
        color="primary"
        disabled={loading}
        startIcon={
          loading ? <CircularProgress size={16} color="inherit" /> : null
        }
      >
        {i18next.t('confirm')}
      </Button>
    </DialogActions>
  </Dialog>
);

WithdrawApprovalDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};
const flattenApprovers = (assignedApprovers) => {
  if (!assignedApprovers) return [];
  if (Array.isArray(assignedApprovers)) return assignedApprovers;
  return Object.values(assignedApprovers).flatMap(
    (level) => level?.approvers ?? [],
  );
};

const flattenLoiApprovers = (assignedApprovers) => {
  if (!assignedApprovers) return [];
  const levels = Array.isArray(assignedApprovers)
    ? assignedApprovers
    : Object.values(assignedApprovers);

  return levels.flatMap((level) =>
    Array.isArray(level?.approvers) ? level.approvers : [level],
  );
};

const Tenders = ({
  pid,
  entryData,
  loiRows = [],
  loiGate = null,
  orderTemplates = [],
  withdrawLoiApproval,
  tender,
  withdrawSentOrder,
  markAsSignOrder,
  deleteOrder,
  quoteFilesForTender,
  withdrawOrderApproval,
  reloadOrders,
  userInfo,
  fetchOrderLogs,
  fetchLoiLogs,
  sendApprovalReminder,
  sendLoiApprovalReminder,
  clinkAccount,
}) => {
  const [open, setOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const { t } = useTranslation();
  const [openLogModal, setOpenLogModal] = useState(false);
  const [orderLogs, setOrderLogs] = useState([]);
  const [loiLogModal, setLoiLogModal] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [finalOrder, setFinalOrder] = useState(null);
  const [finalOrderError, setFinalOrderError] = useState(null);
  const [rejectionAnchorEl, setRejectionAnchorEl] = useState(null);
  const [activeRejection, setActiveRejection] = useState(null);
  const [withdrawApprovalModalOpen, setWithdrawApprovalModalOpen] =
    useState(false);
  const [withdrawApprovalDid, setWithdrawApprovalDid] = useState(null);
  const [withdrawApprovalLoading, setWithdrawApprovalLoading] = useState(false);
  const handleDisabled = () => {
    // Set state to true on button click
    setDisabled(true);

    // Use setTimeout to set state back to false after  1 second
    setTimeout(() => {
      setDisabled(false);
    }, 1000); //  1000 milliseconds =  1 second
  };

  const handleLogModal = useCallback(
    (document_id) => {
      setOpenLogModal(true);
      fetchOrderLogs(document_id).then((result) => {
        setOrderLogs(result?.payload ?? {});
      });
    },
    [fetchOrderLogs],
  );

  const handleLoiLogModal = useCallback(
    (row) => {
      // Open immediately so the modal's own empty state covers the fetch.
      setLoiLogModal({
        logs: [],
        entity_no: row.loi_number,
        package_name: tender?.label,
      });

      if (!fetchLoiLogs) return;
      fetchLoiLogs(row.loi_id).then((result) => {
        const payload = result?.payload?.logs ?? {};
        setLoiLogModal({
          logs: payload.logs ?? [],
          entity_no: row.loi_number ?? payload.entity_no,
          package_name: payload.package_name ?? tender?.label,
        });
      });
    },
    [fetchLoiLogs, tender],
  );

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleWithdrawLoiApproval = useCallback(
    (row) => {
      setOpen({
        id: 'withdraw-loi-approval',
        navTitle: 'withdraw-loi-approval',
        title: 'withdraw-loi-approval-title',
        description: 'withdraw-loi-approval-description',
        cancel: 'cancel',
        confirm: 'confirm',
        handleAccept: () => {
          setOpen(false);
          if (!withdrawLoiApproval) return;
          withdrawLoiApproval({ did: row.loi_id }).then((result) => {
            if (result?.error) {
              setFinalOrderError(
                result.error.message || i18next.t('loi-withdraw-failed'),
              );
              return;
            }
            if (reloadOrders) reloadOrders();
          });
        },
      });
    },
    [withdrawLoiApproval, reloadOrders],
  );

  const handleCreateFinalOrder = useCallback(
    (row) => {
      setFinalOrder({
        quoteInfo: {
          id: row.transaction_id,
          subcontractor: { id: row.subcontractor?.id },
          tender_id: tender?.id,
        },
      });
    },
    [tender],
  );

  const handleRejectionPopoverOpen = (event, row) => {
    setRejectionAnchorEl(event.currentTarget);
    const rejectedApprover = flattenApprovers(row.assigned_approvers)?.find(
      (ap) => ap.status?.label === 'Rejected',
    );
    const did = row.document?.id;
    const tid = tender?.id;
    setActiveRejection({
      type: 'order',
      comment: rejectedApprover?.comment || '—',
      approverName:
        rejectedApprover?.approver_user?.display_name ||
        `${rejectedApprover?.user?.firstname ?? ''} ${rejectedApprover?.user?.lastname ?? ''}`.trim() ||
        '—',
      approverRole: rejectedApprover?.role_label || '',
      editUrl: `/document-creator/template/${did}/order/${tid}`,
    });
  };

  const handleLoiRejectionPopoverOpen = (event, row) => {
    setRejectionAnchorEl(event.currentTarget);
    const rejectedApprover = flattenLoiApprovers(row.assigned_approvers)?.find(
      (ap) => ap.status?.label === 'Rejected',
    );
    const approverUser = rejectedApprover?.approver_user;
    setActiveRejection({
      type: 'loi',
      comment: rejectedApprover?.comment || '—',
      approverName:
        approverUser?.display_name ||
        `${approverUser?.firstname ?? ''} ${approverUser?.lastname ?? ''}`.trim() ||
        '—',
      approverRole: approverUser?.account_role?.label || '',
      editUrl: null,
    });
  };
  const handleRejectionPopoverClose = () => {
    setRejectionAnchorEl(null);
    setActiveRejection(null);
  };
  const openWithdrawApprovalModal = useCallback((did) => {
    setWithdrawApprovalDid(did);
    setWithdrawApprovalModalOpen(true);
  }, []);

  const handleWithdrawApprovalConfirm = useCallback(() => {
    setWithdrawApprovalLoading(true);
    withdrawOrderApproval({ did: withdrawApprovalDid })
      .then(() => {
        setWithdrawApprovalModalOpen(false);
        if (reloadOrders) reloadOrders();
      })
      .finally(() => {
        setWithdrawApprovalLoading(false);
      });
  }, [withdrawOrderApproval, withdrawApprovalDid, reloadOrders]);

  const gateEnabled = Boolean(flag('LOI_ORDER_GATE'));
  const visibleOrders = entryData.filter((row) =>
    isOrderVisible(loiGate, row, gateEnabled),
  );

  const isOrderRequester = (orderRow) =>
    flattenApprovers(orderRow?.assigned_approvers).some(
      (ap) =>
        ap.requester_user_id &&
        clinkAccount?.user?.id &&
        String(ap.requester_user_id) === String(clinkAccount?.user?.id),
    );

  const isLoiRequester = (loiRow) => {
    const requesterId = loiRow?.submitted_by?.id;
    const currentUserId = clinkAccount?.user?.id;
    return Boolean(
      requesterId &&
        currentUserId &&
        String(requesterId) === String(currentUserId),
    );
  };

  return (
    <>
      <Modal open={open} setOpen={setOpen} />
      <WithdrawApprovalDialog
        open={withdrawApprovalModalOpen}
        onClose={() => setWithdrawApprovalModalOpen(false)}
        onConfirm={handleWithdrawApprovalConfirm}
        loading={withdrawApprovalLoading}
      />
      {flag('APPROVAL_THRESHOLD') && (
        <LogsModal
          open={openLogModal}
          onClose={() => setOpenLogModal(false)}
          allLogs={orderLogs?.logs}
          headerTitle={t('order-logs')}
          entity="order"
          entity_no={orderLogs?.entity_no}
          package_name={orderLogs?.package_name}
        />
      )}
      {flag('APPROVAL_THRESHOLD') && (
        <LogsModal
          open={Boolean(loiLogModal)}
          onClose={() => setLoiLogModal(null)}
          allLogs={loiLogModal?.logs}
          headerTitle={t('loi-logs')}
          entity="loi"
          entity_no={loiLogModal?.entity_no}
          package_name={loiLogModal?.package_name}
        />
      )}
      <Modal
        open={
          finalOrder && {
            id: 'create-final-order',
            navTitle: 'create-final-order',
            title: 'choose-an-order',
            backdropClick: true,
          }
        }
        setOpen={() => setFinalOrder(null)}
        style={{ width: '600px' }}
      >
        {finalOrder && (
          <IssueOrderForm
            quoteInfo={finalOrder.quoteInfo}
            orderTemplates={orderTemplates}
            setOpen={() => setFinalOrder(null)}
            onError={setFinalOrderError}
          />
        )}
      </Modal>
      {finalOrderError && (
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          open
          autoHideDuration={8000}
          onClose={() => setFinalOrderError(null)}
        >
          <MuiAlert
            onClose={() => setFinalOrderError(null)}
            severity="error"
            variant="filled"
            elevation={6}
            sx={{ width: '100%' }}
          >
            {finalOrderError}
          </MuiAlert>
        </Snackbar>
      )}
      <RejectionAcknowledgeModal
        anchorEl={rejectionAnchorEl}
        showHeader
        onClose={handleRejectionPopoverClose}
        width={280}
      >
        <RejectionDetailContent
          rejection={activeRejection}
          onClose={handleRejectionPopoverClose}
        />
      </RejectionAcknowledgeModal>
      <ThemeProvider theme={themeTable}>
        <TableContainer component={Paper} data-testid="orders-table">
          <Table>
            <TableHead>
              <TableRow>
                {headEntries.map((headEntry) => (
                  <TableCell key={headEntry.id}>{t(headEntry.entry)}</TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loiRows.map((loiRow) => {
                const rowKey = `loi-${loiRow.loi_id}`;
                const canCreate = canCreateFinalOrder(loiRow, entryData);
                return (
                  <LoiRow
                    key={rowKey}
                    rowKey={rowKey}
                    row={loiRow}
                    expanded={!!expanded[rowKey]}
                    onToggleExpand={toggleExpand}
                    onRejectionClick={handleLoiRejectionPopoverOpen}
                    onCreateFinalOrder={
                      canCreate ? handleCreateFinalOrder : null
                    }
                    onWithdrawApproval={
                      isLoiRequester(loiRow) ? handleWithdrawLoiApproval : null
                    }
                    onViewLogs={handleLoiLogModal}
                    sendApprovalReminder={sendLoiApprovalReminder}
                    canSendReminder={isLoiRequester(loiRow)}
                    showDocumentColumn={Boolean(flag('DELETE_DRAFT_ORDER'))}
                    columnCount={headEntries.length}
                  />
                );
              })}

              {visibleOrders.map((row) => {
                if (isEmpty(row)) {
                  return null;
                }
                const { document, subcontractor, signatory = {} } = row;
                const levels = mapAssignedApproversToLevels(
                  row.assigned_approvers,
                  row.isLevel ?? row.is_level,
                );
                const { signers, total } = signatory;
                const status =
                  PENDING === row.status && total
                    ? `${row.status} (${signers}/${total})`
                    : row.status;
                const documentName = document && document.name;
                const acronym = (documentName ?? '')
                  .split(' ')
                  .filter(Boolean)
                  .reduce(
                    (accumulator, currentValue) =>
                      accumulator + currentValue[0],
                    '',
                  );
                const rowKey = flag('DELETE_DRAFT_ORDER')
                  ? document.id
                  : subcontractor.id;
                const actions = getActions(
                  pid,
                  row,
                  tender,
                  setOpen,
                  quoteFilesForTender,
                  withdrawSentOrder,
                  markAsSignOrder,
                  deleteOrder,
                  [disabled, handleDisabled],
                  handleLogModal,
                  handleLoiLogModal,
                  isOrderRequester(row) ? openWithdrawApprovalModal : null,
                );
                return (
                  <React.Fragment key={`order-${rowKey}`}>
                    <TableRow data-testid={`order-row-${rowKey}`}>
                      <TableCell>
                        <Grid item>
                          {flag('APPROVAL_THRESHOLD') && levels.length > 0 && (
                            <IconButton
                              data-testid={`order-expand-btn-${rowKey}`}
                              aria-label="expand row"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(rowKey);
                              }}
                            >
                              {expanded[rowKey] ? (
                                <KeyboardArrowUpIcon />
                              ) : (
                                <KeyboardArrowDownIcon />
                              )}
                            </IconButton>
                          )}
                          {t('final-order')}
                        </Grid>
                      </TableCell>
                      <TableCell>{row.order_nr}</TableCell>
                      {flag('DELETE_DRAFT_ORDER') && (
                        <TableCell>
                          <Tooltip title={documentName}>
                            <Button>{acronym}</Button>
                          </Tooltip>
                        </TableCell>
                      )}
                      <TableCell>{createdAt(row.created_at)}</TableCell>
                      <TableCell>
                        <Grid container spacing={2}>
                          <AvtarGridContainer>
                            <Avatar
                              src={getAccountLogo(subcontractor.id, true)}
                            />
                          </AvtarGridContainer>
                          <CompanyGridContainer>
                            {subcontractor.name}
                          </CompanyGridContainer>
                        </Grid>
                      </TableCell>
                      <TableCell>
                        {parseCurrency(
                          row.value,
                          currencyConfig[t('currency')],
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusGridContainer>
                          <Grid item>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                              }}
                            >
                              {status}
                              {row.status === 'Rejected' &&
                                !flattenApprovers(row.assigned_approvers).some(
                                  (ap) =>
                                    ap.approver_user_id ===
                                    Number(userInfo?.user?.id),
                                ) && (
                                  <span>
                                    <IconButton
                                      data-testid={`order-rejection-info-${rowKey}`}
                                      size="small"
                                      sx={{ padding: '2px', color: clinkRed }}
                                      onClick={(e) =>
                                        handleRejectionPopoverOpen(e, row)
                                      }
                                    >
                                      <InfoOutlinedIcon size="small" />
                                    </IconButton>
                                  </span>
                                )}
                            </div>
                            <AssignedApproversAvatars
                              assignedApprovers={flattenApprovers(
                                row.assigned_approvers,
                              )}
                            />
                          </Grid>
                          <Grid item>
                            {actions.length > 0 && (
                              <MuiDropdownButton
                                data-testid={`order-actions-${rowKey}`}
                                options={actions}
                              />
                            )}
                          </Grid>
                        </StatusGridContainer>
                      </TableCell>
                    </TableRow>
                    {flag('APPROVAL_THRESHOLD') && (
                      <TableRow data-testid={`order-approval-row-${rowKey}`}>
                        <TableCell colSpan={headEntries.length}>
                          <Collapse
                            in={!!expanded[rowKey]}
                            timeout="auto"
                            unmountOnExit
                          >
                            <ApprovalExpandablePanel
                              levels={levels}
                              sendApprovalReminder={sendApprovalReminder}
                              entity_id={row.document.id}
                              canSendReminder={isOrderRequester(row)}
                            />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </ThemeProvider>
    </>
  );
};
const mapStateToProps = (state) => ({
  clinkAccount: state.clinkAccount,
});

export default connect(mapStateToProps)(Tenders);
